//
// SchemaPinboardPanel
//
// Use to pin definitions, classes, and fields, e.g. while dragging and dropping but
// you want to scroll in between. Can hold more than one item, but shouldn't display
// more than e.g. 5 at a time. And maybe max out at ten.
//
// We can also directly remove the item with an (x) as inline-action
// perhaps clear pinboard in one go?

import { observer } from 'mobx-react-lite'
import { useDrop } from 'react-dnd'
import { useStore } from '../stores'

import { DragSchemaItem } from '../dragdrop/DragSchemaItem'
import { DragTypes } from '../dragdrop/DragTypes'
import { ClassData } from '../stores/data/ClassData'
import { DefinitionData } from '../stores/data/DefinitionData'
import { FieldData } from '../stores/data/FieldData'

import { Spacer, VView } from '../appview'
import { Button, Header, Icon, Text, TreeItem } from '../components'
import { isSameSchemaItem } from '../stores/data/utils'
import { ItemTitle } from '../workspaces/components'

export const SchemaPinboardPanel = observer(function SchemaPinboardPanel({
    pinboard,
    selectedItem,
}) {
    const { app } = useStore()

    const [{ isOver, canDrop }, drop] = useDrop({
        accept: [DragTypes.PIM_DEFINITION, DragTypes.PIM_CLASS, DragTypes.PIM_FIELD],
        canDrop: () => pinboard.canAdd(),
        collect: monitor => {
            return {
                isOver: !!monitor.isOver(),
                canDrop: !!monitor.canDrop(),
            }
        },

        drop: ({ gid }) => {
            pinboard.add(gid)
            return { receiver: 'SchemaPinboardPanel' }
        },
    })

    let classes = 'panel pinboard-panel schema-pinboard-panel pinboard-drop'
    if (isOver) {
        classes += ' drag-over-inside'
        if (!canDrop) classes += ' drop-rejected'
    }

    const pinnedcount = pinboard.pinnedItems.length
    const Message = !pinnedcount ? (
        <Text className="ws-helptext">
            {app.text(
                'Drag a definition, class, or field here for easy access. This pinboard can hold up to ' +
                    pinboard.MAX_ITEMS +
                    ' items.'
            )}
        </Text>
    ) : undefined

    const Items = pinboard.pinnedItems.map(item => {
        const action = (
            <div className="inline-actions">
                <Button
                    icon
                    title={app.text('Remove from pinboard')}
                    onClick={e => {
                        e.preventDefault()
                        e.stopPropagation()
                        pinboard.remove(item.gid)
                    }}
                >
                    <Icon name="remove" size="text" />
                </Button>
            </div>
        )

        const onDrop = dropresult => {
            if (dropresult && dropresult.receiver !== 'SchemaPinboardPanel') {
                pinboard.remove(item.gid)
            }
        }

        const iconname =
            item instanceof DefinitionData
                ? 'definition'
                : item instanceof ClassData
                  ? 'class'
                  : item instanceof FieldData
                    ? 'field'
                    : 'empty'

        return (
            <DragSchemaItem key={item.gid} schemaitem={item} onDrop={onDrop}>
                <TreeItem
                    state={'empty'}
                    emptyicon={iconname}
                    selected={
                        !!selectedItem && isSameSchemaItem(selectedItem, item)
                    }
                    indent={0}
                    onClick={() => pinboard.selectItem(item)}
                >
                    <ItemTitle item={item} ellipsisAfter={40} />
                    {action}
                </TreeItem>
            </DragSchemaItem>
        )
    })

    return (
        <VView ref={drop} className={classes}>
            <Header>{app.text('Schema pinboard')}</Header>
            {Items}
            {Items && !Message ? <Spacer size={5} /> : undefined}
            {Message}
        </VView>
    )
})
